import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LOMGroupPage from "./pages/LOMGroupPage";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardLayout from "./layouts/DashboardLayout";
import DashboardPage from "./pages/DashboardPage";
import ItemsPage from "./pages/ItemsPage";
import WarehousesPage from "./pages/WarehousesPage";
import InventoryPage from "./pages/InventoryPage";
import TransactionsPage from "./pages/TransactionsPage";
import SalesPage from "./pages/SalesPage";
import PurchaseOrdersPage from "./pages/PurchaseOrdersPage";
import SalesOrdersPage from "./pages/SalesOrdersPage";
import ProformaInvoicesPage from "./pages/ProformaInvoicesPage";
import StorageLocationsPage from "./pages/StorageLocationsPage";
import ReportsPage from "./pages/ReportsPage";
import UsersPage from "./pages/UsersPage";
import SettingsPage from "./pages/SettingsPage";
import BillingPage from "./pages/BillingPage";
import AuditLogPage from "./pages/AuditLogPage";
import AdminPage from "./pages/AdminPage";
import SystemSpecsPage from "./pages/SystemSpecsPage";
import TrainingManualPage from "./pages/TrainingManualPage";
import SecurityDocPage from "./pages/SecurityDocPage";
import CustomersPage from "./pages/CustomersPage";
import InstallPrompt from "./components/InstallPrompt";
import PageTracker from "./components/PageTracker";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const AppIndexRedirect = () => {
  const { user } = useAuth();
  if (user?.role === 'super_admin') {
    return <Navigate to="/app/admin" replace />;
  }
  return <Navigate to="/app/dashboard" replace />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to={user.role === 'super_admin' ? '/app/admin' : '/app/dashboard'} replace />;
  }
  
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PageTracker />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LOMGroupPage />} />
          <Route path="/jaminventra" element={<LandingPage />} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Protected App Routes */}
          <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<AppIndexRedirect />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="items" element={<ItemsPage />} />
            <Route path="warehouses" element={<WarehousesPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="transactions" element={<TransactionsPage />} />
            <Route path="sales" element={<SalesPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="sales-orders" element={<SalesOrdersPage />} />
            <Route path="proforma-invoices" element={<ProformaInvoicesPage />} />
            <Route path="storage-locations" element={<StorageLocationsPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="audit-log" element={<AuditLogPage />} />
            <Route path="admin" element={<AdminPage />} />
            <Route path="system-specs" element={<SystemSpecsPage />} />
            <Route path="training-manual" element={<TrainingManualPage />} />
            <Route path="security" element={<SecurityDocPage />} />
          </Route>
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors closeButton />
        <InstallPrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
